"use client";

import { useEffect, useId, useRef, useState } from "react";
import { reportBody, reportDetails, reportMailto, reportSubject } from "@/lib/report";
import type { ReportContext } from "@/lib/report";
import { CONTACT_EMAIL } from "@/lib/site";

/**
 * Report a zman that does not match the reader's luach. Nothing leaves the browser on its
 * own: the report either opens in the reader's own mail program or goes to the clipboard.
 */
export function ReportDialog({
  context,
  onClose,
}: {
  context: ReportContext | null;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const id = useId();
  const [description, setDescription] = useState("");
  const [source, setSource] = useState("");
  const [copied, setCopied] = useState(false);
  const [addressCopied, setAddressCopied] = useState(false);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (context && !dialog.open) dialog.showModal();
    if (!context && dialog.open) dialog.close();
  }, [context]);

  const copy = async () => {
    if (!context) return;
    try {
      await navigator.clipboard.writeText(
        `${reportSubject(context)}\n\n${reportBody({ description, source }, context)}`,
      );
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      setCopied(false);
    }
  };

  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(CONTACT_EMAIL);
      setAddressCopied(true);
      setTimeout(() => setAddressCopied(false), 2500);
    } catch {
      setAddressCopied(false);
    }
  };

  const field = "mt-1 w-full rounded-xl border border-line bg-paper px-3 py-2 focus:outline-none";

  return (
    <dialog
      ref={ref}
      onClose={() => {
        setCopied(false);
        setAddressCopied(false);
        onClose();
      }}
      aria-labelledby={`${id}-title`}
      className="mx-auto mb-0 mt-auto max-h-[85dvh] w-full max-w-lg rounded-t-3xl bg-card p-0 text-ink shadow-2xl backdrop:bg-black/50 sm:mb-auto sm:rounded-3xl"
    >
      {context && (
        <div className="flex max-h-[85dvh] flex-col">
          <header className="flex items-start justify-between gap-4 border-b border-line px-5 py-4">
            <div>
              <h2 id={`${id}-title`} className="font-serif text-2xl font-bold">
                דיווח על אי-התאמה
              </h2>
              <p className="text-sm text-muted">
                מצאתם זמן שאינו תואם ללוח שלכם? זה בדיוק מה שעוזר לתקן.
              </p>
            </div>
            <button
              type="button"
              onClick={() => ref.current?.close()}
              aria-label="סגירה"
              className="inline-flex size-10 items-center justify-center rounded-full text-2xl leading-none text-muted hover:bg-accent-soft"
            >
              ×
            </button>
          </header>

          <div className="space-y-4 overflow-y-auto px-5 py-4 text-sm">
            <label className="block">
              <span className="font-medium">מה לא תואם?</span>
              <textarea
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="למשל: צאת הכוכבים מאוחר אצלכם בשתי דקות."
                className={field}
              />
            </label>
            <label className="block">
              <span className="font-medium">מול איזה לוח או רב?</span>
              <input
                type="text"
                value={source}
                onChange={(e) => setSource(e.target.value)}
                placeholder="למשל: לוח ארץ ישראל, או הרב של בית הכנסת"
                className={field}
              />
            </label>

            <details className="rounded-xl border border-line bg-paper px-3 py-2">
              <summary className="cursor-pointer font-medium">מה יישלח יחד עם הדיווח</summary>
              <pre className="mt-2 overflow-x-auto whitespace-pre-wrap break-words text-xs leading-relaxed text-muted">
                {reportDetails(context)}
              </pre>
            </details>

            <p className="text-xs leading-relaxed text-muted">
              האתר אינו שולח כלום בעצמו. הכפתור פותח את תוכנת הדואר שלכם עם ההודעה מוכנה, ואתם
              שולחים אותה. אם אין תוכנת דואר, אפשר להעתיק את ההודעה ולשלוח אותה מכל מקום — הכתובת
              רשומה למטה.
            </p>
          </div>

          <footer className="space-y-2 border-t border-line px-5 py-4">
            <div className="flex flex-wrap gap-2">
              <a
                href={reportMailto({ description, source }, context)}
                className="inline-flex min-h-11 items-center rounded-xl bg-accent px-4 font-medium text-paper hover:opacity-90"
              >
                שליחה בדואר
              </a>
              <button
                type="button"
                onClick={copy}
                className="inline-flex min-h-11 items-center rounded-xl border border-line px-4 font-medium hover:bg-accent-soft"
              >
                {copied ? "ההודעה הועתקה ✓" : "העתקת ההודעה"}
              </button>
            </div>
            {/* Spelled out, so the report can be sent from anywhere if mailto does nothing. */}
            <p className="text-sm text-muted">
              הכתובת לשליחה:{" "}
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                dir="ltr"
                className="font-medium text-accent underline-offset-2 hover:underline"
              >
                {CONTACT_EMAIL}
              </a>
              <button
                type="button"
                onClick={copyAddress}
                className="ms-2 inline-flex min-h-6 items-center px-1 text-xs font-medium text-accent underline-offset-2 hover:underline"
              >
                {addressCopied ? "הועתקה ✓" : "העתקה"}
              </button>
            </p>
          </footer>
        </div>
      )}
    </dialog>
  );
}
